import { useMemo } from "react";
import {
  IPartialCountryCode,
  IDocument,
  PartyDocumentNameCode,
  PartyDocumentNameCodeSpaced,
} from "@vmosolutions/oc-interfaces"; //IAircraftType
import { Formik, FormikProps, FormikHelpers } from "formik"; //FieldArray
import { FormikTextField, FormikSelectField, FormikDateTimeField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Alert, Button, ButtonGroup, Grid, Typography } from "@mui/material";
import * as Yup from "yup";
import { IActiveModal } from "src/pages/Admin/AdminWindow";

interface IAPISDocumentProps {
  selectedDocument: IDocument;
  updateDocument: (document: IDocument, mode: "create" | "update" | "delete") => void;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  countryCodeOptions: IPartialCountryCode[];
  setOpenDocumentModal: any;
  setSelectedDocument: any;
}

const initialFieldValues: IDocument = {
  documentType: PartyDocumentNameCode.Passport,
  documentNumber: "",
  expirationDate: undefined,
  placeOfDocumentIssue: "",
};

export const getEnumValueFromKey = (enumerable: any, value: string): string => {
  return Object.keys(enumerable).find(
    (key: string) => PartyDocumentNameCode[key as keyof typeof PartyDocumentNameCode].toString() === value
  );
};

const partyDocumentOptions = Object.keys(PartyDocumentNameCodeSpaced)
  .map((key) => {
    return {
      label: key, //Passport
      value: PartyDocumentNameCodeSpaced[key as keyof typeof PartyDocumentNameCodeSpaced], //P
    };
  })
  .sort((a, b) => {
    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
  });

// Configuration revision validation with YUP
export const APISFieldsSchema = Yup.object().shape({
  documentType: Yup.mixed().oneOf(Object.values(PartyDocumentNameCode)).required("Document Type is required"),
  documentNumber: Yup.string()
    .matches(/^[a-zA-Z0-9-_]+$/, "Must be alphanumeric, dashes and underscores are accepted as well.")
    .max(35, "the maximum length cannot exceed 35 characters")
    .required("Document Number is required"),
  expirationDate: Yup.string(),
  placeOfDocumentIssue: Yup.string().required("Place Of Document Issue is required").length(3),
});

type DocumentForm = FormikProps<IDocument>;

/**
 * Form to edit selected aircraft configuration revision
 * @param props - selected aircraft
 */
export const APISDocumentField = (props: IAPISDocumentProps): JSX.Element => {
  const {
    selectedDocument,
    setActiveModal,
    updateDocument,
    countryCodeOptions,
    setOpenDocumentModal,
    setSelectedDocument,
  } = props;

  const initialValues = useMemo(() => {
    return selectedDocument ? selectedDocument : initialFieldValues;
  }, [selectedDocument]);

  const handleSubmit = (values: IDocument, helpers: FormikHelpers<IDocument>): void => {
    const { setStatus, resetForm } = helpers;
    const mode = selectedDocument ? "update" : "create";
    updateDocument(values, mode);
    resetForm({ values });
    setStatus({ success: true });
    setOpenDocumentModal(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={APISFieldsSchema}
    >
      {(APISSubForm: DocumentForm) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = APISSubForm;

        //this is causing a warning :(
        // setDocumentFormIsDirty(dirty);

        const handleDelete = (): void => {
          setOpenDocumentModal(false);
          if (!selectedDocument) {
            return;
          }
          setActiveModal({
            title: "Delete Document",
            message: "Are you certain you wish to delete this document?",
            cancel: () => {
              setActiveModal(null);
              setOpenDocumentModal(true);
            },
            confirm: (): void => {
              setActiveModal(null);
              updateDocument(selectedDocument, "delete");
              setSelectedDocument(null);
              APISSubForm.resetForm({ values: initialFieldValues });
              APISSubForm.setStatus({ success: true });
            },
            confirmLabel: "Delete",
          });
        };

        return (
          <Grid container>
            <Grid item xs={12} sx={{ pb: 2 }}>
              <Alert severity="info" variant="filled">
                After staging document changes, you must save the main form for the changes to take effect.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Manage Documents</Typography>
              <FormikSelectField
                name="documentType"
                label="Document Type"
                parse={(option: any) => option.value}
                format={(documentType: string) =>
                  partyDocumentOptions.find((option: any) => option.value === documentType)
                }
                getOptionLabel={(option: any) => `${option.value} - ${option.label}`}
                options={partyDocumentOptions}
                closeMenuOnSelect={false}
                isSearchable={true}
                isClearable={false}
                menuPosition="fixed"
              />
              <FormikTextField label="Document Number" name="documentNumber" type="string" />
              <FormikSelectField
                name="placeOfDocumentIssue"
                label="Country Of Document Issue"
                parse={(option: IPartialCountryCode) => option.alphaThreeCode}
                format={(countryCode: string) =>
                  countryCodeOptions?.find(
                    (opt: IPartialCountryCode) => opt?.alphaThreeCode?.toUpperCase() === countryCode?.toUpperCase()
                  )
                }
                options={countryCodeOptions}
                getOptionLabel={(option: IPartialCountryCode) => `${option.countryName} - ${option.alphaThreeCode}`}
                getOptionValue={(option: IPartialCountryCode) => `${option.alphaThreeCode}`}
                closeMenuOnSelect={false}
                isClearable={false}
                menuPosition="fixed"
              />

              <FormikDateTimeField label="Expiration Date" name="expirationDate" hideTimeControl={true} />
            </Grid>

            <Grid item xs={12} sx={{ pt: 3, display: "flex", justifyContent: "space-between" }}>
              <ButtonGroup variant="contained" size="small">
                <Button
                  color="success"
                  disabled={isSubmitting || !isValid || !dirty}
                  type="submit"
                  onClick={() => handleSubmit(values, APISSubForm)}
                >
                  {selectedDocument ? "Stage Update" : "Stage Creation"}
                </Button>
                <Button
                  type="reset"
                  color="warning"
                  disabled={(!dirty && isValid) || isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
                {selectedDocument && (
                  <Button color="error" disabled={!selectedDocument || isSubmitting} onClick={handleDelete}>
                    Delete
                  </Button>
                )}
              </ButtonGroup>
              <Button variant="contained" color="error" size="small" onClick={() => setOpenDocumentModal(false)}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
};
